import {MultiFilePicker} from '@github-ui/copilot-spaces/components/MultiFilePicker'
import type {CopilotSpaceRepositoryResource, GitHubFileFormData} from '@github-ui/copilot-spaces/types'
import {fetchRepo} from '@github-ui/copilot-spaces/utils/fetch-repo'
import {useCallback, useRef} from 'react'

import {makeFileReference, makeRepositoryReference} from '../utils/copilot-chat-helpers'
import type {CopilotChatRepo} from '../utils/copilot-chat-types'
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'

interface CopilotFilePickerProps {
  isOpen: boolean
  onClose: () => void
  initialRepo?: CopilotChatRepo | null
  onRepoSelect?: (repo: CopilotChatRepo | null) => void
}

export function CopilotFilePicker({isOpen, onClose, initialRepo, onRepoSelect}: CopilotFilePickerProps) {
  const manager = useChatManager()
  const {findFileWorkerPath} = useChatState()
  const attachmentButtonRef = useRef<HTMLButtonElement>(null)

  const handleSaveRepository = useCallback(
    async (repoResource: CopilotSpaceRepositoryResource) => {
      const repo = await fetchRepo(repoResource.repositoryId)
      const repositoryReference = makeRepositoryReference(repo)
      manager.addReference(repositoryReference, 'references-menu')
      onClose()
    },
    [manager, onClose],
  )

  const handleSaveFiles = useCallback(
    async (formData: GitHubFileFormData[]) => {
      if (formData.length === 0) {
        onClose()
        return
      }

      for (const item of formData) {
        const repo = await fetchRepo(item.repositoryId)
        const reference = makeFileReference(item.filePath, repo)

        manager.addReference(reference, 'references-menu')
      }

      onClose()
    },
    [manager, onClose],
  )

  if (!isOpen) {
    return null
  }

  return (
    <MultiFilePicker
      formData={[]}
      onCancel={onClose}
      onSaveFiles={handleSaveFiles}
      onSaveRepository={handleSaveRepository}
      findFileWorkerPath={findFileWorkerPath}
      attachmentButtonRef={attachmentButtonRef}
      initialRepo={initialRepo}
      onRepoSelect={onRepoSelect}
    />
  )
}

try{ CopilotFilePicker.displayName ||= 'CopilotFilePicker' } catch {}